import { isEqual } from 'radash'
import { immer } from 'zustand/middleware/immer'
import { StateCreator } from 'zustand/vanilla'

import { SearchOffersDto } from '@wwt/entities/api/search-response'
import { searchRequestAdapterForRemote } from '@wwt/shared/utils/adapters'

import { assert } from '../../../shared/lib/assert.ts'
import { SearchRequest } from '../index.ts'

type State = {
	/**
	 * Body for search request
	 */
	searchRequest: SearchRequest | null
	/**
	 * Value in store was setted
	 *
	 * It can be not changed, but setted
	 */
	isDirty: boolean
}

type Actions = {
	/**
	 * Set body for search request
	 */
	setSearchRequest: (searchRequest: SearchRequest | null) => void

	/**
	 * Set body for search request without check on same
	 *
	 * Uses for update params after click on `Apply` button
	 */
	setForceSearchRequest: (searchRequest: SearchRequest | null) => void

	/**
	 * Transforming {@link SearchRequest} to {@link SearchOffersDto} and return
	 */
	getForAPI: () => SearchOffersDto | null

	/**
	 * - Returns `true` if store.searchRequest = null
	 * - Returns `false` if not
	 */
	checkStoreIsEmptyAndNotTouched: () => boolean

	/**
	 * Change sort value
	 * Uses for auto-search after update sort
	 */
	setSort: (sort: string) => void
}

export type SearchAPIStore = State & Actions

/**
 * Store for save body for search request
 *
 * **Why we need this?**
 *
 * Actually we need some **external** data store to save search request body
 * because when using local variables(like `useState`) inside `useSearch`,
 * when switching between pages, we have additional unnecessary and
 * incomprehensible requests that are difficult to debug
 *
 * This happens through a full rerender and re-initialization of the data
 * inside the page and hook
 *
 * The simplest and most understandable solution is to store the search data
 * separately from the search hook, this allows us to correctly compare
 * different data and search only and only when we really need it
 *
 * Zustand is perfect for this role
 *
 * @author Ivan Shchedrovskyi
 */
export const searchApiBodyStore: StateCreator<
	SearchAPIStore,
	[],
	[['zustand/immer', never]]
> = immer((set, get) => ({
	searchRequest: null,
	isDirty: false,

	getForAPI: () => {
		const searchRequest = get().searchRequest

		if (searchRequest == null) {
			return null
		}

		return searchRequestAdapterForRemote(searchRequest)
	},

	checkStoreIsEmptyAndNotTouched: () => {
		return get().searchRequest === null && get().isDirty === false
	},

	setForceSearchRequest: searchRequest => {
		set(state => {
			// TODO: Remove structuredClone
			// eslint-disable-next-line
			state.searchRequest = structuredClone(searchRequest)
			state.isDirty = true
		})
	},

	setSearchRequest: searchRequest => {
		set(state => {
			if (searchRequest === null) {
				state.isDirty = true
				return
			}

			const isSameParams = isEqual(state.searchRequest, searchRequest)

			if (isSameParams) return

			state.searchRequest = searchRequest
			state.isDirty = true
		})
	},

	setSort: sortBy => {
		set(state => {
			assert(
				state.searchRequest !== null,
				'Search request is undefined on set sort'
			)

			state.searchRequest.sortBy = sortBy
		})
	}
}))
